import React from 'react';
import ProfessionalCard from './ProfessionalCard';

/**
 * ProfessionalList Component
 * Displays a scrollable list of matched professionals/contractors
 * Can be embedded in the chat messages
 * 
 * @param {Array} professionals - Array of professional objects
 * @param {String} recommendation - 'DIY', 'CONTRACTOR', or 'CONSULTANT'
 * @param {Object} criteria - Matching criteria (issueType, severity, etc.); "Book Now" carries trade + severity from it
 * @param {String} searchQuery - Search query for web search fallback
 * @param {Boolean} noResults - Flag indicating if no results were found
 */
export default function ProfessionalList({ professionals, recommendation, criteria, searchQuery, noResults }) {
  // Check if we have external contractors
  const hasExternalContractors = professionals && professionals.some(p => p.isExternal);
  const externalCount = professionals ? professionals.filter(p => p.isExternal).length : 0;
  const verifiedCount = professionals ? professionals.filter(p => !p.isExternal).length : 0;

  if (recommendation === 'DIY') {
    return (
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border border-green-200 my-3">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-green-900 mb-1">DIY Solution Available</h4>
            <p className="text-sm text-green-800">
              This is a simple issue you can likely handle yourself. The AI Inspector will guide you through the steps!
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Info banner for external contractors
  if (hasExternalContractors && externalCount > 0) {
    return (
      <>
        {/* External Contractors Info Banner */}
        <div className="bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg p-4 border-2 border-orange-200 my-3 shadow-sm">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0">
              <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="flex-1">
              <h4 className="font-semibold text-orange-900 mb-2">
                {verifiedCount > 0 ? `${verifiedCount} Verified + ${externalCount} Found Online` : `${externalCount} Contractors Found Online`}
              </h4>
              <p className="text-sm text-orange-800 mb-2">
                {verifiedCount === 0 
                  ? `We couldn't find verified contractors in your area, but we found ${externalCount} professionals online.`
                  : `We found ${verifiedCount} verified contractors and ${externalCount} additional professionals online.`
                }
              </p>
              <div className="bg-white/60 rounded-md p-3 space-y-1.5">
                <p className="text-xs text-orange-900 font-medium">When you request booking:</p>
                <ul className="text-xs text-orange-800 space-y-1 ml-4">
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 mt-0.5">✓</span>
                    <span>We'll send them an invitation to join HOA Connect</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 mt-0.5">✓</span>
                    <span>They'll receive your booking request with job details</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 mt-0.5">✓</span>
                    <span>You'll be notified when they respond (usually within 24 hours)</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="text-orange-600 mt-0.5">✓</span>
                    <span>Once they join, you can book them directly on the platform</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Contractor Cards */}
        <div className="space-y-3 px-4">
          {professionals.map((professional) => (
            <ProfessionalCard
              key={professional.id}
              professional={professional}
              criteria={criteria}
            />
          ))}
        </div>
      </>
    );
  }

  if (!professionals || professionals.length === 0) {
    const handleWebSearch = () => {
      // Open Google search in a new tab with the search query
      const encodedQuery = encodeURIComponent(searchQuery || 'contractors near me');
      window.open(`https://www.google.com/search?q=${encodedQuery}`, '_blank');
    };

    return (
      <div className="bg-yellow-50 rounded-lg p-4 border border-yellow-200 my-3">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0">
            <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
          </div>
          <div className="flex-1">
            <h4 className="font-semibold text-yellow-900 mb-2">No Professionals Found in Our Database</h4>
            <p className="text-sm text-yellow-800 mb-3">
              We couldn't find any verified professionals matching your criteria at the moment. 
              Try searching the web for local contractors or consultants.
            </p>
            <button
              onClick={handleWebSearch}
              className="w-full bg-white border-2 border-yellow-400 text-yellow-900 px-4 py-2 rounded-lg font-medium text-sm hover:bg-yellow-50 transition-colors flex items-center justify-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              Search Web for Contractors
            </button>
            <p className="text-xs text-yellow-700 mt-2">
              💡 <strong>Tip:</strong> After finding contractors online, you can invite them to join our platform for future bookings!
            </p>
          </div>
        </div>
      </div>
    );
  }

  const professionalTypeLabel = recommendation === 'CONSULTANT' ? 'Consultants' : 'Contractors';

  return (
    <div className="my-3">
      {/* Header */}
      <div className="mb-3">
        <h3 className="font-semibold text-gray-900 text-base mb-1">
          {professionals.length} {professionalTypeLabel} Available
        </h3>
        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
          {criteria?.issueType && (
            <span className="inline-flex items-center px-2 py-1 rounded bg-gray-100">
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
              </svg>
              {criteria.issueType}
            </span>
          )}
          {criteria?.severity && (
            <span className={`inline-flex items-center px-2 py-1 rounded ${
              ['SEVERE', 'MAJOR', 'EMERGENCY'].includes(criteria.severity)
                ? 'bg-red-100 text-red-800'
                : ['MODERATE'].includes(criteria.severity)
                ? 'bg-orange-100 text-orange-800'
                : 'bg-blue-100 text-blue-800'
            }`}>
              {criteria.severity}
            </span>
          )}
          {criteria?.maxDistance && (
            <span className="inline-flex items-center px-2 py-1 rounded bg-gray-100">
              <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              Within {criteria.maxDistance} km
            </span>
          )}
        </div>
      </div>

      {/* Professional Cards - Scrollable Horizontal Carousel with Snap */}
      <div className="w-full max-w-full overflow-hidden">
        <div 
          className="flex gap-3 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-2 px-2" 
          style={{
            scrollPaddingLeft: '0.5rem',
            scrollPaddingRight: '0.5rem',
            WebkitOverflowScrolling: 'touch',
            maxWidth: '100vw'
          }}
        >
          {professionals.map((professional) => (
            <div 
              key={professional.id} 
              className="flex-shrink-0 snap-center"
              style={{
                width: 'calc(100vw - 3rem)',
                maxWidth: '360px'
              }}
            >
              <ProfessionalCard professional={professional} criteria={criteria} />
            </div>
          ))}
        </div>
      </div>

      {/* Helper Text */}
      <p className="text-xs text-gray-500 mt-2">
        <svg className="w-3 h-3 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        All professionals are verified and insured. Scroll to see more options.
      </p>
    </div>
  );
}

